import { Search, Loader2 } from "lucide-react";

/**
 * Product lookup tab (right rail in ChatsPage).
 *
 * Renders three sections:
 *   1. Country indicator banner — tells the agent INR vs USD context.
 *   2. Auto-detected product chips — extracted from product URLs in chat.
 *   3. Manual lookup input + the rich result card with category badge,
 *      variants matrix, dual-price cards and copy/send actions.
 *
 * Pure presentational — all state and handlers come in via props.
 */
export const ProductLookupTab = ({
  priceScope,
  detectedProducts,
  productLookup, setProductLookup,
  productLoading,
  productData, setProductData,
  productSuggestions, setProductSuggestions,
  lookupProduct,
  selectedLead,
  buildProductDetailsMessage,
  copyProductSummary,
  sendProductSummary,
}) => {
  return (
    <div className="p-4" data-testid="product-lookup-section">
      {/* Country indicator — drives which price agent should share */}
      <div
        className={`mb-3 p-2 rounded-lg text-[10px] font-bold ${priceScope === "USD" ? "bg-indigo-50 text-indigo-800 border border-indigo-200" : "bg-emerald-50 text-emerald-800 border border-emerald-200"}`}
        data-testid="price-scope-indicator"
      >
        {priceScope === "USD"
          ? "🇺🇸 US number — share USD price only (includes free worldwide shipping)"
          : "🇮🇳 India number — share INR price only"}
      </div>

      {/* Auto-detected products from chat URLs */}
      {detectedProducts.length > 0 && (
        <div className="mb-3 p-3 bg-purple-50 rounded-xl border border-purple-200 animate-fade-up" data-testid="detected-products">
          <p className="text-[10px] font-bold text-purple-700 uppercase tracking-wider mb-2">
            Auto-detected from chat links ({detectedProducts.length})
          </p>
          <div className="space-y-1.5">
            {detectedProducts.map(p => (
              <button
                key={p.itemcode}
                onClick={() => { setProductLookup(p.itemcode); lookupProduct(p.itemcode); }}
                className="w-full text-left p-2 bg-white rounded-lg hover:bg-purple-100 border border-purple-100 transition-colors"
                data-testid={`detected-product-${p.itemcode}`}
              >
                <div className="flex items-center justify-between gap-2">
                  <div className="min-w-0 flex-1">
                    <p className="text-[11px] font-bold text-[--text-primary] truncate">{p.title}</p>
                    <p className="text-[9px] text-[--text-muted] font-mono">{p.itemcode}{p.category ? ` · ${p.category}` : ""}</p>
                  </div>
                  <span className="text-[9px] text-purple-600 font-bold flex-shrink-0">Look up →</span>
                </div>
              </button>
            ))}
          </div>
        </div>
      )}

      <p className="text-[10px] font-bold text-[--text-muted] uppercase tracking-wider mb-2">Manual Lookup</p>
      <p className="text-[10px] text-[--text-muted] mb-3">
        Enter an item code (e.g. <span className="font-mono font-bold">OZZ702</span>, <span className="font-mono font-bold">HBC042</span>).
      </p>
      <div className="flex gap-2 mb-3">
        <input
          type="text"
          value={productLookup}
          onChange={(e) => setProductLookup(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") lookupProduct(); }}
          placeholder="Enter item code..."
          className="flex-1 border-2 border-[--border] rounded-xl px-3 py-2 text-xs uppercase outline-none focus:border-[--accent-soft]"
          data-testid="product-lookup-input"
        />
        <button
          onClick={() => lookupProduct()}
          disabled={productLoading || !productLookup.trim()}
          className="btn-primary text-xs px-3"
          data-testid="product-lookup-btn"
        >
          {productLoading ? <Loader2 size={12} className="animate-spin" /> : <Search size={12} />}
        </button>
      </div>

      {productData && productData.raw && (
        <ProductDetailsCard
          productData={productData}
          priceScope={priceScope}
          onClear={() => { setProductData(null); setProductLookup(""); if (setProductSuggestions) setProductSuggestions(null); }}
          buildProductDetailsMessage={buildProductDetailsMessage}
          copyProductSummary={copyProductSummary}
          sendProductSummary={sendProductSummary}
          selectedLead={selectedLead}
        />
      )}

      {/* "Did you mean…?" chips — shown on lookup miss when the user typed a
          code that didn't exact-match. Click a chip → re-runs lookup with
          the exact code. */}
      {!productData && Array.isArray(productSuggestions) && productSuggestions.length > 0 && (
        <div className="mt-3 p-3 rounded-xl border border-amber-200 bg-amber-50 animate-fade-up" data-testid="product-suggestions">
          <p className="text-[10px] font-bold text-amber-700 uppercase tracking-wider mb-2">
            No exact match — did you mean?
          </p>
          <div className="flex flex-wrap gap-1.5">
            {productSuggestions.map((s) => (
              <button
                key={s.itemcode}
                onClick={() => { setProductLookup(s.itemcode); lookupProduct(s.itemcode); }}
                className="text-[10px] font-bold px-2 py-1 rounded-full bg-white border border-amber-300 hover:bg-amber-100 text-amber-900 inline-flex items-center gap-1"
                data-testid={`product-suggestion-${s.itemcode}`}
              >
                <span className="font-mono">{s.itemcode}</span>
                {s.category && <span className="text-amber-600 normal-case font-normal">· {s.category}</span>}
              </button>
            ))}
          </div>
        </div>
      )}
      {!productData && Array.isArray(productSuggestions) && productSuggestions.length === 0 && (
        <p className="mt-3 text-[10px] text-rose-600" data-testid="product-no-suggestions">
          No exact match found and no close suggestions either. Double-check the item code.
        </p>
      )}
    </div>
  );
};

const ProductDetailsCard = ({
  productData,
  priceScope,
  onClear,
  buildProductDetailsMessage,
  copyProductSummary,
  sendProductSummary,
  selectedLead,
}) => {
  const det = productData.raw.details || {};
  const pri = productData.raw.pricing || {};
  const log = productData.raw.logistics || {};
  const title = det.title || det.name || productData.itemcode;
  // Top-level `category` from upstream is the single source of truth.
  // Falls back to nested fields only for legacy payloads.
  const category = (productData.category || productData.raw.category || det.category || det.product_type || "").toString();
  const isBook = productData.is_book ?? category.toLowerCase().includes("book");
  const variants = Array.isArray(productData.variants) ? productData.variants : [];
  const weightDisplay = productData.logistics_resolved?.weight_display;
  // Iter 156.49 — authoritative website prices (all-in incl. S&H) from
  // admin_crm product fetch. Null when the upstream call failed → cards
  // fall back to vendor-derived values with an "approx" caution.
  const auth = productData.authoritative || {};

  return (
    <div className="space-y-2 animate-fade-up" data-testid="product-details">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex items-start gap-2">
          {/* Product thumbnail — sourced from upstream details.image (or
              top-level raw.image as fallback). Hidden via onError so a
              broken/blocked CDN URL doesn't leave an ugly placeholder. */}
          {(det.image || productData.raw?.image) && (
            <a
              href={det.image || productData.raw.image}
              target="_blank"
              rel="noopener noreferrer"
              className="flex-shrink-0 block rounded-lg overflow-hidden border border-[--border] bg-white hover:ring-2 hover:ring-[--accent] transition-all"
              data-testid="product-image-link"
            >
              <img
                src={det.image || productData.raw.image}
                alt={title}
                className="w-14 h-14 object-cover"
                loading="lazy"
                onError={(e) => { e.currentTarget.parentElement.style.display = "none"; }}
                data-testid="product-image"
              />
            </a>
          )}
          <div className="min-w-0">
            <p className="text-sm font-bold text-[--text-primary] truncate" style={{ fontFamily: "Outfit" }}>{title}</p>
            <div className="flex items-center gap-2 flex-wrap mt-0.5">
              <p className="text-[10px] text-[--text-muted] font-bold font-mono">{productData.itemcode}</p>
              {category && (
                <span className="text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-full bg-violet-100 text-violet-700 border border-violet-200" data-testid="product-category-badge">
                  {category}
                </span>
              )}
              {variants.length > 1 && (
                <span className="text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700 border border-amber-200" data-testid="product-variants-badge">
                  {variants.length} variants
                </span>
              )}
            </div>
          </div>
        </div>
        <button onClick={onClear} className="text-[10px] text-[--text-muted] hover:text-[--accent] flex-shrink-0" data-testid="product-clear-btn">Clear</button>
      </div>

      <div className="p-3 rounded-xl border bg-[--bg-cream] border-[--border] space-y-1.5">
        {det.title && <DetailRow label="Title" value={det.title} testId="product-title" />}
        {det.author && <DetailRow label="Author" value={det.author} />}
        {det.publisher && <DetailRow label="Publisher" value={det.publisher} />}
        {det.pages && <DetailRow label="Pages" value={det.pages} />}
        {det.language && <DetailRow label="Language" value={det.language} />}
        {det.edition && <DetailRow label="Edition" value={det.edition} />}
        {det.binding && <DetailRow label="Binding" value={det.binding} />}
        {det.isbn && <DetailRow label="ISBN" value={det.isbn} mono />}
        {det.color && <DetailRow label="Color" value={det.color} testId="product-color" />}
        {det.size && <DetailRow label="Size" value={det.size} testId="product-size" />}
        {det.dimensions && <DetailRow label="Dimensions" value={det.dimensions} testId="product-dimensions" />}
        {weightDisplay && <DetailRow label="Weight" value={weightDisplay} testId="product-weight" />}
        {/* PA — Product Availability flag.
            PA=1: product is always live on the website (we can procure it
              even if local_stock=0; effectively "made-to-order ready in
              leadtime days").
            PA=0 + local_stock=0: out of stock on the website.
            We render a friendly status pill rather than the raw 0/1 number. */}
        {productData.raw.pa != null && (
          <PAStatusRow
            pa={productData.raw.pa}
            localStock={productData.logistics_resolved?.local_stock}
            leadtime={productData.logistics_resolved?.leadtime_days}
          />
        )}
      </div>

      {/* Variants matrix — only when 2+ */}
      {variants.length > 1 && (
        <VariantsList
          variants={variants}
          crmVariations={(priceScope === "USD" ? auth.usd?.variations : auth.inr?.variations) || null}
          crmSymbol={priceScope === "USD" ? "$" : "₹"}
        />
      )}

      {/* Dual-price cards — country-aware. Active card is highlighted. */}
      {(pri.website_india_price || pri.website_dollar_price || auth.inr || auth.usd) && (
        <div className="grid grid-cols-2 gap-2">
          {(pri.website_india_price || auth.inr) && (
            <PriceCardINR pri={pri} priceScope={priceScope} authInr={auth.inr} />
          )}
          {(pri.website_dollar_price || auth.usd) && (
            <PriceCardUSD pri={pri} priceScope={priceScope} authUsd={auth.usd} />
          )}
        </div>
      )}

      {/* Global FX prices — real per-country website prices when available
          (Iter 156.49, admin_crm product fetch), with the locally-computed
          estimate as fallback. GBP/EUR show "Sales only" until VAT rules
          are finalised. */}
      {((productData.fx_prices && productData.fx_prices.base_usd != null) || Object.keys(auth.fx || {}).length > 0) && (
        <FXPricesCard fxPrices={productData.fx_prices || {}} authFx={auth.fx || {}} />
      )}

      {/* Availability / leadtime */}
      {(log.leadtime || log.local_stock) && (
        <div className="p-3 rounded-xl border border-green-200 bg-green-50 space-y-1">
          <p className="text-[9px] font-bold text-green-700 uppercase">Availability</p>
          {log.leadtime && <div className="text-[11px] text-green-900">Ships within <span className="font-bold">{parseInt(log.leadtime)} days</span> of order</div>}
          {log.local_stock && <div className="text-[11px] text-green-900"><span className="font-bold">{parseInt(log.local_stock)}</span> units in local stock</div>}
        </div>
      )}

      {/* WhatsApp-ready summary — DETAILS ONLY, no price (per business rule) */}
      {productData.summary && (
        <div className="p-3 rounded-xl border border-[--border] bg-white space-y-2">
          <p className="text-[9px] font-bold text-[--text-muted] uppercase">WhatsApp-Ready Summary (no price)</p>
          <pre className="text-[11px] whitespace-pre-wrap font-[Nunito] text-[--text-primary] bg-[--bg-cream] rounded-lg p-2 max-h-40 overflow-y-auto" data-testid="product-summary">
            {buildProductDetailsMessage(productData)}
          </pre>
          <div className="flex gap-2">
            <button onClick={copyProductSummary} className="flex-1 btn-secondary text-[11px] py-1.5" data-testid="product-copy-btn">Copy</button>
            <button
              onClick={sendProductSummary}
              disabled={!selectedLead}
              className="flex-1 btn-primary text-[11px] py-1.5 disabled:opacity-50"
              data-testid="product-send-btn"
            >
              Send to chat
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

const DetailRow = ({ label, value, mono, testId }) => (
  <div className="text-[11px]" data-testid={testId}>
    <span className="text-[--text-muted]">{label}: </span>
    <span className={`font-bold text-[--text-primary]${mono ? " font-mono" : ""}`}>{value}</span>
  </div>
);

/**
 * PA (Product Availability) status badge.
 *
 * Business rule from Yash @ Exotic India (Apr 2026):
 *   - PA=1: product is ALWAYS live on the website. If local_stock>0 we ship
 *     immediately; if local_stock=0 we still accept the order and procure
 *     within `leadtime_days`.
 *   - PA=0 + local_stock=0: out of stock on the website (cannot order).
 *   - PA=0 + local_stock>0: in stock from local inventory only.
 */
const PAStatusRow = ({ pa, localStock, leadtime }) => {
  const paFlag = String(pa) === "1" || pa === 1 || pa === true;
  const stock = Number(localStock) || 0;

  let badge;
  if (paFlag) {
    badge = stock > 0
      ? { text: "Always live · in local stock", cls: "bg-emerald-100 text-emerald-700 border-emerald-200" }
      : { text: `Always live · made-to-order in ${leadtime ?? "—"} days`, cls: "bg-emerald-50 text-emerald-700 border-emerald-200" };
  } else {
    badge = stock > 0
      ? { text: "Limited · in local stock only", cls: "bg-amber-100 text-amber-700 border-amber-200" }
      : { text: "Out of stock on website", cls: "bg-rose-100 text-rose-700 border-rose-200" };
  }

  return (
    <div className="text-[11px] flex items-center gap-2" data-testid="product-pa-row">
      <span className="text-[--text-muted]">Availability flag:</span>
      <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[9px] font-bold uppercase tracking-wider border ${badge.cls}`}>
        PA={paFlag ? "1" : "0"} · {badge.text}
      </span>
    </div>
  );
};

const VariantsList = ({ variants, crmVariations, crmSymbol }) => {
  // Iter 156.49 — match the authoritative per-variation price (real website
  // all-in figure) by size/color label; falls back to no price when absent.
  const crmPriceFor = (v) => {
    if (!crmVariations) return null;
    const cands = [v.size, v.color, [v.color, v.size].filter(Boolean).join(" / ")]
      .filter(Boolean).map((s) => s.trim().toLowerCase());
    for (const [key, cv] of Object.entries(crmVariations)) {
      if (cands.includes(key.trim().replace(/:$/, "").trim().toLowerCase())) {
        const p = parseFloat(cv?.price);
        if (!Number.isNaN(p) && p > 0) return p;
      }
    }
    return null;
  };
  return (
    <div className="p-3 rounded-xl border border-amber-200 bg-amber-50" data-testid="product-variants-list">
      <p className="text-[9px] font-bold text-amber-700 uppercase mb-1.5">Available Variants</p>
      <div className="space-y-1">
        {variants.map((v, i) => {
          const label = [v.color, v.size].filter(Boolean).join(" / ") || `Variant ${i + 1}`;
          const stock = v.local_stock != null ? v.local_stock : null;
          const crmPrice = crmPriceFor(v);
          return (
            <div key={`${label}-${i}`} className="flex items-center justify-between text-[11px]" data-testid={`variant-row-${i}`}>
              <span className="text-amber-900 font-medium truncate">{label}</span>
              <span className="flex items-center gap-2 flex-shrink-0 ml-2">
                {crmPrice != null && (
                  <span className="text-[10px] font-bold text-indigo-800 font-mono" data-testid={`variant-price-${i}`}>
                    {crmSymbol}{Number(crmPrice).toLocaleString()}
                  </span>
                )}
                <span className={`text-[10px] font-bold ${stock > 0 ? "text-emerald-700" : "text-rose-700"}`}>
                  {stock != null ? (stock > 0 ? `${stock} in stock` : "Out of stock") : "—"}
                </span>
              </span>
            </div>
          );
        })}
      </div>
      {crmVariations && (
        <p className="text-[8px] text-amber-600 mt-1.5">Prices are live website all-in figures (incl. S&H)</p>
      )}
    </div>
  );
};

const PriceCardINR = ({ pri, priceScope, authInr }) => {
  const mrp = parseFloat(pri.website_india_price) || 0;
  const disc = parseFloat(pri.discount_india) || 0;
  const sell = disc > 0 ? Math.round(mrp * (1 - disc / 100)) : mrp;
  const active = priceScope === "INR";
  // Iter 156.49 — authoritative all-in website price (incl. S&H) from
  // admin_crm. The vendor `website_india_price` is a BASE figure that
  // EXCLUDES shipping & handling — only shown as fallback with a caution.
  const authPrice = parseFloat(authInr?.price);
  const hasAuth = !Number.isNaN(authPrice) && authPrice > 0;
  return (
    <div className={`p-2.5 rounded-xl border-2 ${active ? "border-emerald-400 bg-emerald-50" : "border-gray-200 bg-gray-50 opacity-60"}`} data-testid="price-card-inr">
      <div className="flex items-center justify-between">
        <p className="text-[9px] font-bold text-emerald-700 uppercase">🇮🇳 INR price</p>
        {active && <span className="text-[9px] font-bold text-emerald-600">✓ FOR THIS LEAD</span>}
      </div>
      {hasAuth ? (
        <>
          <p className="text-sm font-bold text-emerald-900 mt-1" data-testid="price-inr-authoritative">₹ {Math.round(authPrice).toLocaleString()}</p>
          <p className="text-[9px] text-emerald-700 mt-0.5">Live website price · all-in (incl. shipping & handling)</p>
          {mrp > 0 && Math.round(authPrice) !== sell && (
            <p className="text-[8px] text-emerald-600 opacity-70">base ₹{sell} + S&H</p>
          )}
        </>
      ) : (
        <>
          {disc > 0 ? (
            <>
              <p className="text-[10px] text-emerald-800 mt-1 line-through opacity-70">₹ {mrp} (MRP)</p>
              <p className="text-sm font-bold text-emerald-900">₹ {sell} <span className="text-[9px] font-bold text-rose-600">−{disc}% off</span></p>
            </>
          ) : (
            <p className="text-sm font-bold text-emerald-900 mt-1">₹ {mrp}</p>
          )}
          <p className="text-[9px] text-amber-700 mt-0.5 font-bold" data-testid="price-inr-base-warning">
            ⚠ Base price — may EXCLUDE shipping & handling (live price unavailable)
          </p>
        </>
      )}
      {!active && <p className="text-[9px] text-red-600 mt-1 font-bold">Do NOT send to US lead</p>}
    </div>
  );
};

const PriceCardUSD = ({ pri, priceScope, authUsd }) => {
  const mrpUsd = parseFloat(pri.website_dollar_price) || 0;
  const discGl = parseFloat(pri.discount_global) || 0;
  const sellUsd = discGl > 0 ? (mrpUsd * (1 - discGl / 100)).toFixed(2) : mrpUsd.toFixed(2);
  const active = priceScope === "USD";
  const authPrice = parseFloat(authUsd?.price);
  const hasAuth = !Number.isNaN(authPrice) && authPrice > 0;
  return (
    <div className={`p-2.5 rounded-xl border-2 ${active ? "border-indigo-400 bg-indigo-50" : "border-gray-200 bg-gray-50 opacity-60"}`} data-testid="price-card-usd">
      <div className="flex items-center justify-between">
        <p className="text-[9px] font-bold text-indigo-700 uppercase">🇺🇸 USD price</p>
        {active && <span className="text-[9px] font-bold text-indigo-600">✓ FOR THIS LEAD</span>}
      </div>
      {hasAuth ? (
        <>
          <p className="text-sm font-bold text-indigo-900 mt-1" data-testid="price-usd-authoritative">$ {Number(authPrice).toLocaleString()}</p>
          <p className="text-[9px] text-indigo-700 mt-0.5">Live website price · free worldwide shipping</p>
        </>
      ) : (
        <>
          {discGl > 0 ? (
            <>
              <p className="text-[10px] text-indigo-800 mt-1 line-through opacity-70">$ {mrpUsd} (MRP)</p>
              <p className="text-sm font-bold text-indigo-900">$ {sellUsd} <span className="text-[9px] font-bold text-rose-600">−{discGl}% off</span></p>
            </>
          ) : (
            <p className="text-sm font-bold text-indigo-900 mt-1">$ {mrpUsd}</p>
          )}
          <p className="text-[9px] text-amber-700 mt-0.5 font-bold">⚠ Approx — live price unavailable</p>
        </>
      )}
      {!active && <p className="text-[9px] text-red-600 mt-1 font-bold">US numbers only</p>}
    </div>
  );
};


/**
 * FXPricesCard — at-a-glance grid of post-discount converted prices for every
 * non-India currency the team accepts (Stripe + PayPal corridors).
 *
 * Source: backend `fx_prices` block on /api/books/{itemcode}.
 *   • base_usd is `price_dollar` × (1 - discount_global%) — the FX anchor.
 *   • Each currency amount = base_usd × USD-INR / TARGET-INR, rounded half-up.
 *   • GBP / EUR are intentionally unavailable until the team finalises
 *     VAT-inclusive pricing — we render them muted with a "Sales only" badge.
 *
 * The agent uses this card as a quick answer to "what should I quote in <X>?"
 * No copy/send action — the text is small enough to read off and type.
 */
const CURRENCY_META = {
  USD: { flag: "🇺🇸", symbol: "$", name: "US Dollar" },
  AED: { flag: "🇦🇪", symbol: "AED", name: "UAE Dirham" },
  AUD: { flag: "🇦🇺", symbol: "A$", name: "Australian $" },
  CAD: { flag: "🇨🇦", symbol: "C$", name: "Canadian $" },
  CHF: { flag: "🇨🇭", symbol: "CHF", name: "Swiss Franc" },
  CNY: { flag: "🇨🇳", symbol: "¥", name: "Chinese Yuan" },
  EUR: { flag: "🇪🇺", symbol: "€", name: "Euro" },
  GBP: { flag: "🇬🇧", symbol: "£", name: "British Pound" },
  HKD: { flag: "🇭🇰", symbol: "HK$", name: "Hong Kong $" },
  JPY: { flag: "🇯🇵", symbol: "¥", name: "Japanese Yen" },
  KRW: { flag: "🇰🇷", symbol: "₩", name: "Korean Won" },
  MYR: { flag: "🇲🇾", symbol: "RM", name: "Malaysian Ringgit" },
  NPR: { flag: "🇳🇵", symbol: "Rs", name: "Nepali Rupee" },
  NZD: { flag: "🇳🇿", symbol: "NZ$", name: "New Zealand $" },
  RUB: { flag: "🇷🇺", symbol: "₽", name: "Russian Rouble" },
  SGD: { flag: "🇸🇬", symbol: "S$", name: "Singapore $" },
  THB: { flag: "🇹🇭", symbol: "฿", name: "Thai Baht" },
};

const FXPricesCard = ({ fxPrices, authFx }) => {
  // Iter 156.49 — merge: authoritative live website prices (admin_crm, per
  // accessing country) win; locally-computed estimates fill the gaps.
  const live = authFx || {};
  const computed = fxPrices.currencies || {};
  const codes = Array.from(new Set([...Object.keys(live), ...Object.keys(computed)]));
  if (codes.length === 0) return null;
  const sorted = codes.sort((a, b) => {
    if (a === "USD") return -1;
    if (b === "USD") return 1;
    return a.localeCompare(b);
  });
  const liveCount = Object.keys(live).length;

  return (
    <div className="p-3 rounded-xl border border-indigo-200 bg-indigo-50/40" data-testid="fx-prices-card">
      <div className="flex items-baseline justify-between mb-2">
        <p className="text-[10px] font-bold text-indigo-700 uppercase tracking-wider">
          Global FX prices · {sorted.length} currencies
        </p>
        {liveCount > 0 && (
          <p className="text-[9px] text-emerald-600 font-bold" data-testid="fx-live-badge">
            ● {liveCount} live
          </p>
        )}
      </div>
      <p className="text-[9px] text-indigo-600 mb-2 leading-snug">
        <span className="font-bold text-emerald-700">Live</span> = real website price for that country (all-in).{" "}
        <span className="font-bold text-amber-700">Approx</span> = FX estimate. Use these for non-US overseas customers paying in their currency.
      </p>
      <div className="grid grid-cols-3 gap-1.5">
        {sorted.map((code) => {
          const meta = CURRENCY_META[code] || { flag: "", symbol: code, name: code };
          const liveInfo = live[code];
          const info = liveInfo || computed[code];
          if (info?.unavailable) {
            return (
              <div
                key={code}
                className="p-1.5 rounded-lg border border-amber-200 bg-amber-50 flex flex-col items-start"
                data-testid={`fx-price-${code}`}
                title={info.reason || "Unavailable"}
              >
                <span className="text-[9px] font-bold text-amber-700">
                  {meta.flag} {code}
                </span>
                <span className="text-[10px] font-bold text-amber-800 leading-tight">
                  Sales only
                </span>
                <span className="text-[8px] text-amber-600 leading-tight">
                  VAT pending
                </span>
              </div>
            );
          }
          if (info?.amount == null) return null;
          return (
            <div
              key={code}
              className={`p-1.5 rounded-lg border flex flex-col items-start transition-colors ${liveInfo ? "border-emerald-300 bg-white hover:bg-emerald-50" : "border-indigo-200 bg-white hover:bg-indigo-50"}`}
              data-testid={`fx-price-${code}`}
            >
              <span className={`text-[9px] font-bold ${liveInfo ? "text-emerald-700" : "text-indigo-700"}`}>
                {meta.flag} {code} {liveInfo ? "●" : ""}
              </span>
              <span className="text-[11px] font-bold text-indigo-900 leading-tight font-mono">
                {meta.symbol} {Number(info.amount).toLocaleString()}
              </span>
              {!liveInfo && (
                <span className="text-[8px] text-amber-600 leading-tight">approx</span>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};
